import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import LeavePolicyService, {
  type LeavePolicyDto,
  type SaveLeavePolicyDto,
} from "@/services/api/leavePolicy";
import { handleResponse } from "@/services/axios";

interface LeavePolicyState {
  policies: LeavePolicyDto[];
  isLoading: boolean;
  isSaving: boolean;
  isToggling: boolean;
  error: string | null;
}

const initialState: LeavePolicyState = {
  policies: [],
  isLoading: false,
  isSaving: false,
  isToggling: false,
  error: null,
};

export const getLeavePolicies = createAsyncThunk(
  "leavePolicy/getLeavePolicies",
  async (_, { rejectWithValue }) => {
    try {
      const res = await LeavePolicyService.getLeavePolicies();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to fetch leave policies"
      );
    }
  }
);

export const createLeavePolicy = createAsyncThunk(
  "leavePolicy/createLeavePolicy",
  async (payload: SaveLeavePolicyDto, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeavePolicyService.createLeavePolicy(payload);
      const data = handleResponse(res, rejectWithValue);
      dispatch(getLeavePolicies());
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to create leave policy"
      );
    }
  }
);

export const updateLeavePolicy = createAsyncThunk(
  "leavePolicy/updateLeavePolicy",
  async (payload: SaveLeavePolicyDto, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeavePolicyService.updateLeavePolicy(payload);
      const data = handleResponse(res, rejectWithValue);
      dispatch(getLeavePolicies());
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to update leave policy"
      );
    }
  }
);

export const toggleLeavePolicyStatus = createAsyncThunk(
  "leavePolicy/toggleLeavePolicyStatus",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeavePolicyService.toggleLeavePolicyStatus(id);
      const data = handleResponse(res, rejectWithValue);
      dispatch(getLeavePolicies());
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to toggle leave policy status"
      );
    }
  }
);

const leavePolicySlice = createSlice({
  name: "leavePolicy",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // getLeavePolicies
      .addCase(getLeavePolicies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLeavePolicies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.policies = action.payload || [];
      })
      .addCase(getLeavePolicies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // createLeavePolicy
      .addCase(createLeavePolicy.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(createLeavePolicy.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(createLeavePolicy.rejected, (state) => {
        state.isSaving = false;
      })
      // updateLeavePolicy
      .addCase(updateLeavePolicy.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateLeavePolicy.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(updateLeavePolicy.rejected, (state) => {
        state.isSaving = false;
      })
      // toggleLeavePolicyStatus
      .addCase(toggleLeavePolicyStatus.pending, (state) => {
        state.isToggling = true;
      })
      .addCase(toggleLeavePolicyStatus.fulfilled, (state) => {
        state.isToggling = false;
      })
      .addCase(toggleLeavePolicyStatus.rejected, (state) => {
        state.isToggling = false;
      });
  },
});

export default leavePolicySlice.reducer;
